import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useApp } from "@/contexts/AppContext";

interface Summary {
  alerts: number;
  visitors_1d: number | null;
  users_1d: number | null;
  listings_new_1d: number | null;
  listings_pending: number | null;
  reveals_1d: number | null;
  deals_confirmed_7d: number | null;
  messages_1d: number | null;
  mrr_brl: number | null;
  site_ok: boolean | null;
  site_ms: number | null;
}
interface ReportRow {
  id: string;
  created_at: string;
  alert_count: number;
  alerts: string[];
  summary: Summary | null;
  html?: string;
}

const PAGE = 30;
const fmtDate = (s: string) =>
  new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const num = (v: number | null | undefined) => (v == null ? "—" : Number(v).toLocaleString("pt-BR"));
const brl = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

function useIsAdmin() {
  const { user, authLoading } = useApp();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    if (authLoading) return;
    if (!user) { setIsAdmin(false); return; }
    supabase.rpc("get_my_profile").select("is_admin").single()
      .then(({ data }: { data: { is_admin?: boolean } | null }) => setIsAdmin(!!data?.is_admin));
  }, [user?.id, authLoading]);
  return { isAdmin, loading: authLoading || isAdmin === null };
}

function AlertBadge({ n }: { n: number }) {
  return n > 0 ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#fbf1dc] px-2 py-0.5 text-[11px] font-bold text-[#94680f]">
      <AlertTriangle className="h-3 w-3" /> {n} alerta{n > 1 ? "s" : ""}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#eaf2ec] px-2 py-0.5 text-[11px] font-bold text-[#2d7a4d]">
      <CheckCircle2 className="h-3 w-3" /> tudo em ordem
    </span>
  );
}

/* ---------------- lista ---------------- */
function ReportList() {
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(true);

  async function load(offset: number) {
    setLoading(true);
    const { data } = await supabase
      .from("admin_reports")
      .select("id, created_at, alert_count, alerts, summary")
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE - 1);
    const list = (data ?? []) as ReportRow[];
    setRows((prev) => (offset === 0 ? list : [...prev, ...list]));
    setMore(list.length === PAGE);
    setLoading(false);
  }
  useEffect(() => { load(0); }, []);

  return (
    <div className="space-y-2.5">
      {rows.map((r) => {
        const s = r.summary;
        return (
          <Link key={r.id} to={`/admin/relatorios/${r.id}`}
            className="block rounded-2xl border border-[#ebe7dc] bg-white p-4 transition hover:-translate-y-0.5 hover:border-[#2d7a4d] hover:shadow-[0_8px_24px_rgba(26,74,46,0.08)]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold capitalize text-[#1f2621]">{fmtDate(r.created_at)}</span>
              <AlertBadge n={r.alert_count} />
            </div>
            {r.alert_count > 0 && (
              <p className="mt-1.5 line-clamp-2 text-xs text-[#94680f]">{(r.alerts || []).join(" · ")}</p>
            )}
            {s && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#8a9088]">
                <span>👥 <b className="text-[#1f2621]">{num(s.visitors_1d)}</b> visitantes</span>
                <span>⏳ <b className="text-[#1f2621]">{num(s.listings_pending)}</b> aguardando</span>
                <span>📞 <b className="text-[#1f2621]">{num(s.reveals_1d)}</b> contatos</span>
                <span>💬 <b className="text-[#1f2621]">{num(s.messages_1d)}</b> mensagens</span>
                <span>💰 <b className="text-[#1f2621]">{brl(s.mrr_brl)}</b></span>
                <span>🌐 {s.site_ok === false ? <b className="text-[#c0493b]">site com erro</b> : `${num(s.site_ms)} ms`}</span>
              </div>
            )}
          </Link>
        );
      })}
      {loading && <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[#2d7a4d]" /></div>}
      {!loading && rows.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-[#ebe7dc] bg-white py-12 text-center">
          <FileText className="h-8 w-8 text-[#2d7a4d]" />
          <div className="font-semibold text-[#1a4a2e]">Nenhum relatório ainda</div>
          <div className="text-sm text-[#8a9088]">Eles são gerados às 10h, 14h, 18h e 22h.</div>
        </div>
      )}
      {!loading && more && rows.length > 0 && (
        <button onClick={() => load(rows.length)}
          className="w-full rounded-xl border border-[#ebe7dc] bg-white py-2.5 text-sm font-semibold text-[#2d7a4d] hover:bg-[#eaf2ec]">
          Carregar mais
        </button>
      )}
    </div>
  );
}

/* ---------------- um relatorio ---------------- */
function ReportView({ id }: { id: string }) {
  const [row, setRow] = useState<ReportRow | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [prevId, setPrevId] = useState<string | null>(null);
  const [nextId, setNextId] = useState<string | null>(null);
  const [height, setHeight] = useState(1600);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setRow(null); setNotFound(false); setPrevId(null); setNextId(null);
    (async () => {
      const { data } = await supabase.from("admin_reports").select("id, created_at, alert_count, alerts, html").eq("id", id).maybeSingle();
      if (!data) { setNotFound(true); return; }
      setRow(data as ReportRow);
      const [older, newer] = await Promise.all([
        supabase.from("admin_reports").select("id").lt("created_at", data.created_at).order("created_at", { ascending: false }).limit(1),
        supabase.from("admin_reports").select("id").gt("created_at", data.created_at).order("created_at", { ascending: true }).limit(1),
      ]);
      setPrevId(older.data?.[0]?.id ?? null);
      setNextId(newer.data?.[0]?.id ?? null);
    })();
  }, [id]);

  if (notFound) return <div className="rounded-2xl border border-[#ebe7dc] bg-white py-12 text-center text-sm text-[#8a9088]">Relatório não encontrado.</div>;
  if (!row) return <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[#2d7a4d]" /></div>;

  // links do relatorio abrem em nova aba; nenhum script roda dentro do quadro
  const doc = (row.html || "").replace(/<html>/i, '<html><head><meta charset="utf-8"><base target="_blank"></head>');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold capitalize text-[#1f2621]">{fmtDate(row.created_at)}</span>
          <AlertBadge n={row.alert_count} />
        </div>
        <div className="flex gap-2">
          <Link to={prevId ? `/admin/relatorios/${prevId}` : "#"} aria-disabled={!prevId}
            className={`inline-flex items-center gap-1 rounded-lg border border-[#ebe7dc] bg-white px-3 py-1.5 text-xs font-semibold ${prevId ? "text-[#2d7a4d] hover:bg-[#eaf2ec]" : "pointer-events-none text-[#c5c9c3]"}`}>
            <ChevronLeft className="h-3.5 w-3.5" /> Anterior
          </Link>
          <Link to={nextId ? `/admin/relatorios/${nextId}` : "#"} aria-disabled={!nextId}
            className={`inline-flex items-center gap-1 rounded-lg border border-[#ebe7dc] bg-white px-3 py-1.5 text-xs font-semibold ${nextId ? "text-[#2d7a4d] hover:bg-[#eaf2ec]" : "pointer-events-none text-[#c5c9c3]"}`}>
            Próximo <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
      <iframe
        ref={frame}
        title="Relatório"
        srcDoc={doc}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        className="w-full rounded-2xl border border-[#ebe7dc] bg-[#f7f5ef]"
        style={{ height }}
        onLoad={() => {
          const h = frame.current?.contentDocument?.documentElement?.scrollHeight;
          if (h) setHeight(h + 8);
        }}
      />
    </div>
  );
}

/* ---------------- pagina ---------------- */
export default function AdminReportsPage() {
  const { id } = useParams();
  const { isAdmin, loading } = useIsAdmin();

  if (loading) return <div className="container mx-auto px-4 py-20 text-center text-sm text-[#8a9088]">Verificando permissões...</div>;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-[#1a4a2e]">Relatórios</h1>
          <p className="text-[13px] text-[#8a9088]">Gerados automaticamente às 10h, 14h, 18h e 22h · guardados por 1 ano</p>
        </div>
        <Link to={id ? "/admin/relatorios" : "/admin"}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-[#2d7a4d] hover:bg-[#eaf2ec]">
          <ArrowLeft className="h-4 w-4" /> {id ? "Todos os relatórios" : "Painel admin"}
        </Link>
      </div>
      {id ? <ReportView id={id} /> : <ReportList />}
    </div>
  );
}
